import { useCallback, useEffect, useRef } from 'react';
import {
  CurrencyFormatterOptions,
  intlNumberFormatter,
  useFormattedInput,
} from 'react-formatted-input-hook';

import {
  CURRENCIES_META,
  CurrencyCodeType,
} from '@mint-lib/currencies-helpers';
import { useTranslation } from '@mint-lib/i18n';
import {
  CurrencyFormatType,
  ProfileLocale,
  useProfile,
} from '@mint-lib/profile-helpers';

import TextField, { TextFieldProps } from '../TextField/TextField.jsx';

export interface CurrencyFieldProps extends Omit<
  TextFieldProps,
  'value' | 'onChange'
> {
  value?: number | null;
  onChange?: (value: number | null, formattedValue: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  maxDecimals?: number;
  currency: string;
  restFormatProps?: Omit<
    CurrencyFormatterOptions,
    'location' | 'value' | 'onChange' | 'currency'
  > & { minDecimals?: number };
}
/**
 * A text field component for currency input.
 *
 * @remarks
 * This component uses the `react-formatted-input-hook` library to format and validate currency input.
 *
 * @param value - The value of the input. Should be a number or null.
 * @param onChange - A function that will be called when the input value changes. The first argument is the new value (a number or null), and the second argument is the formatted value (a string).
 * @param maxDecimals - The maximum number of decimal places allowed in the input.
 * @param restFormatProps - Additional options to pass to the `currencyFormatter` function from `react-formatted-input-hook`.
 * @param currency - The currency code to use for formatting. Should be a three-letter ISO 4217 currency code (e.g. "USD").
 * @param autoFocus - If `true`, the number field will be focused on mount.
 * @param restProps - Additional props to pass to the underlying `TextField` component from `@mui/material`.
 * @param onFocus - The function to call when the number field gains focus.
 * @param onBlur - The function to call when the number field loses focus.
 *
 * @throws Error if the `currency` prop is not a valid three-letter ISO 4217 currency code.
 *
 * @example
 * ```tsx
 * import CurrencyField from '@myn/mui';
 *
 * function MyComponent() {
 *   const [value, setValue] = useState<number | null>(null);
 *
 *   const handleChange = (newValue: number | null, formattedValue: string) => {
 *     setValue(newValue);
 *   };
 *
 *   return (
 *     <CurrencyField
 *       value={value}
 *       onChange={handleChange}
 *       maxDecimals={2}
 *       currency="USD"
 *       label="Enter amount"
 *     />
 *   );
 * }
 * ```
 */

const CURRENCY_FORMAT_MAP: Record<ProfileLocale, CurrencyFormatType> = {
  'en-GB': 'before',
  'en-US': 'before',
  'pt-BR': 'before',
  'it-IT': 'after',
  FAKE: 'after',
};

const CurrencyField = ({
  value,
  onChange,
  onFocus,
  onBlur,
  maxDecimals = 2,
  restFormatProps = { minDecimals: 2 },
  currency,
  autoFocus,
  ...restProps
}: CurrencyFieldProps) => {
  const { i18n } = useTranslation('@myn/mui');

  const { getProfileField } = useProfile();

  const threeCapitalLetter = /^[A-Z]{3}$/g;

  if (!threeCapitalLetter.test(currency)) {
    throw Error('Currency should be in standard currency format like USD');
  }

  const currencySymbol = CURRENCIES_META[currency as CurrencyCodeType].symbol;

  const locale = (
    getProfileField('formattingConfig')?.numbersFormat
      ? getProfileField('formattingConfig').numbersFormat
      : i18n.language
  ) as string;

  // By default BE returns null or '' if user selected auto format
  // in this case we should use locale as value for mapping
  //  it to CURRENCY_FORMAT_MAP
  const formatLocale = getProfileField('locale')
    ? getProfileField('locale')
    : i18n.language;

  const currencyFormat =
    getProfileField('formattingConfig')?.currencyFormat ||
    CURRENCY_FORMAT_MAP[formatLocale as ProfileLocale];

  const prevLocaleRef = useRef(locale);
  const prevCurrencyFormateRef = useRef(currencyFormat);

  const formattedInput = useFormattedInput(
    intlNumberFormatter({
      ...restFormatProps,
      locales: locale,
      value,
      onChange,
      maxDecimals,
      liveUpdate: true,
      prefix: currencyFormat === 'before' ? currencySymbol : undefined,
      suffix: currencyFormat === 'after' ? ` ${currencySymbol}` : undefined,
    }),
  );

  const {
    ref,
    onFocus: libOnFocus,
    onBlur: libOnBlur,
    ...rest
  } = formattedInput.props;

  // A workaround hook to reactive update formatting when user changes config
  useEffect(() => {
    if (
      (prevLocaleRef.current !== locale ||
        prevCurrencyFormateRef.current !== currencyFormat) &&
      formattedInput.props.ref.current
    ) {
      formattedInput.props.ref.current.focus();
      prevLocaleRef.current = locale;
      prevCurrencyFormateRef.current = currencyFormat;
    }
  }, [locale, currencyFormat]);

  // A workaround for the issue with the `autoFocus` prop. You can read more about it here: https://github.com/nick-keller/react-formatted-input-hook/issues/3
  useEffect(() => {
    if (formattedInput.props.ref.current && autoFocus === true) {
      formattedInput.props.ref.current.focus();
    }
  }, [formattedInput.props.ref.current == null, autoFocus]);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(event);
      }
      libOnFocus(event);
    },
    [onFocus, libOnFocus],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(event);
      }
      libOnBlur(event);
    },
    [onBlur, libOnBlur],
  );

  return (
    <TextField
      {...restProps}
      InputProps={{
        onFocus: handleFocus,
        onBlur: handleBlur,
        ...rest,
        inputRef: ref,
      }}
    />
  );
};

export default CurrencyField;
